/**
 * Geo pricing & second-chance visibility by Shopify Markets country.
 * Config: window.dbodhiGeoConfig (theme-js-config.liquid).
 */
(function () {
  'use strict';

  const config = window.dbodhiGeoConfig;
  if (!config) return;

  const allowedCountries = config.allowedCountries || [];
  const hiddenSecondChanceCountries = config.hiddenSecondChanceCountries || [];
  const restrictionText = config.restrictionText || '';
  const secondChanceCollectionHandle = config.secondChanceCollectionHandle || 'second-chance';
  const secondChanceRedirectUrl = config.secondChanceRedirectUrl || '/';
  const shopifyCountryCode = config.shopifyCountryCode || '';

  const geoPurchaseRestrictionState = {
    isRestricted: false,
    message: restrictionText,
  };

  const geoSecondChanceState = {
    isHidden: false,
    hasRedirected: false,
  };

  let geoSecondChanceMutationObserver = null;
  let geoSecondChanceMutationTimer = null;

  function isCartAddUrl(value) {
    if (!value) return false;
    const normalized = String(value).toLowerCase();
    return normalized.includes('/cart/add');
  }

  function shouldBlockAddToCartForm(formEl) {
    if (!formEl || !(formEl instanceof HTMLFormElement)) return false;
    const actionAttr = formEl.getAttribute('action') || '';
    if (isCartAddUrl(actionAttr)) return true;
    if (formEl.matches('.product-information-form, [data-type="add-to-cart-form"]')) return true;
    if (formEl.closest('product-form') && formEl.querySelector('input[name="id"]')) return true;
    return false;
  }

  function parseRequestUrl(input) {
    if (!input) return '';
    if (typeof input === 'string') return input;
    if (typeof URL !== 'undefined' && input instanceof URL) return input.href || '';
    if (typeof Request !== 'undefined' && input instanceof Request) return input.url || '';
    if (typeof input.url === 'string') return input.url;
    return '';
  }

  function applyGeoPriceVisibility(canSeePrice) {
    const isRestricted = !canSeePrice;
    geoPurchaseRestrictionState.isRestricted = isRestricted;
    window.geoPurchaseRestriction = geoPurchaseRestrictionState;

    document.body.classList.remove('geo-price-pending');
    document.body.classList.toggle('geo-hide-prices', isRestricted);
    document.body.dataset.hidePricesForGeo = String(isRestricted);
    syncGeoPurchaseRestrictionMessage(isRestricted);
  }

  function syncGeoPurchaseRestrictionMessage(isRestricted) {
    const productForms = document.querySelectorAll('product-form.product-form, .product-form');

    productForms.forEach((productForm) => {
      const submitTargets = productForm.querySelectorAll('.product-form__submit, [data-submit-btn]');
      const deliveryTargets = productForm.querySelectorAll('[data-delivery-text]');
      const oosTargets = productForm.querySelectorAll('[data-oos-wrapper]');

      let messageEl = productForm.querySelector('.geo-country-restriction-message');

      if (!messageEl) {
        messageEl = document.createElement('p');
        messageEl.className = 'geo-country-restriction-message';
        messageEl.textContent = restrictionText;

        const deliveryTextEl = productForm.querySelector('[data-delivery-text]');
        if (deliveryTextEl && deliveryTextEl.parentNode) {
          deliveryTextEl.parentNode.insertBefore(messageEl, deliveryTextEl);
        } else {
          const submitWrap = productForm.querySelector('[data-submit-btn]');
          if (submitWrap && submitWrap.parentNode) {
            submitWrap.parentNode.insertBefore(messageEl, submitWrap.nextSibling);
          } else {
            productForm.appendChild(messageEl);
          }
        }
      } else {
        messageEl.textContent = restrictionText;
      }

      submitTargets.forEach((el) => {
        if (isRestricted) {
          if (el.dataset.geoOriginalDisplay === undefined) {
            el.dataset.geoOriginalDisplay = el.style.display || '';
          }
          el.style.display = 'none';
          if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
            el.disabled = true;
            el.setAttribute('aria-disabled', 'true');
          }
        } else {
          const originalDisplay = el.dataset.geoOriginalDisplay;
          if (originalDisplay !== undefined) {
            el.style.display = originalDisplay;
            delete el.dataset.geoOriginalDisplay;
            if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
              el.disabled = false;
              el.removeAttribute('aria-disabled');
            }
          }
        }
      });

      deliveryTargets.forEach((el) => {
        if (isRestricted) {
          if (el.dataset.geoOriginalDisplay === undefined) {
            el.dataset.geoOriginalDisplay = el.style.display || '';
          }
          el.style.display = 'none';
        } else {
          const originalDisplay = el.dataset.geoOriginalDisplay;
          if (originalDisplay !== undefined) {
            el.style.display = originalDisplay;
            delete el.dataset.geoOriginalDisplay;
          }
        }
      });

      oosTargets.forEach((el) => {
        if (isRestricted) {
          if (el.dataset.geoOriginalDisplay === undefined) {
            el.dataset.geoOriginalDisplay = el.style.display || '';
          }
          el.style.display = 'none';
        } else {
          const originalDisplay = el.dataset.geoOriginalDisplay;
          if (originalDisplay !== undefined) {
            el.style.display = originalDisplay;
            delete el.dataset.geoOriginalDisplay;
          }
        }
      });

      messageEl.style.display = isRestricted ? 'block' : 'none';
    });
  }

  function applySecondChanceVisibility(shouldHide) {
    geoSecondChanceState.isHidden = shouldHide;

    const secondChanceMenuTargets = document.querySelectorAll(
      `[data-collection-handle="${secondChanceCollectionHandle}"], [data-submenu-handle="${secondChanceCollectionHandle}"]`
    );

    secondChanceMenuTargets.forEach((el) => {
      if (shouldHide) {
        if (el.dataset.geoSecondChanceOriginalDisplay === undefined) {
          el.dataset.geoSecondChanceOriginalDisplay = el.style.display || '';
        }
        el.style.display = 'none';
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('tabindex', '-1');
      } else {
        const originalDisplay = el.dataset.geoSecondChanceOriginalDisplay;
        if (originalDisplay !== undefined) {
          el.style.display = originalDisplay;
          delete el.dataset.geoSecondChanceOriginalDisplay;
        } else {
          el.style.removeProperty('display');
        }
        el.removeAttribute('aria-hidden');
        el.removeAttribute('tabindex');
      }
    });

    const restrictedProductCards = document.querySelectorAll(
      '.product-thumbnail[data-product-tag-type="second-chance"], .product-thumbnail[data-product-tag-type="discontinued-stock"], .card-wrapper[data-product-tag-type="second-chance"], .card-wrapper[data-product-tag-type="discontinued-stock"]'
    );
    const restrictedProductTargets = new Set();

    restrictedProductCards.forEach((cardEl) => {
      const hideTarget =
        cardEl.closest('li') ||
        cardEl.closest('.grid__item') ||
        cardEl.closest('.product-grid__item') ||
        cardEl.closest('.swiper-slide') ||
        cardEl;
      restrictedProductTargets.add(hideTarget);
    });

    restrictedProductTargets.forEach((targetEl) => {
      if (shouldHide) {
        if (targetEl.dataset.geoSecondChanceOriginalDisplay === undefined) {
          targetEl.dataset.geoSecondChanceOriginalDisplay = targetEl.style.display || '';
        }
        targetEl.style.display = 'none';
      } else {
        const originalDisplay = targetEl.dataset.geoSecondChanceOriginalDisplay;
        if (originalDisplay !== undefined) {
          targetEl.style.display = originalDisplay;
          delete targetEl.dataset.geoSecondChanceOriginalDisplay;
        } else {
          targetEl.style.removeProperty('display');
        }
      }
    });

    const isSecondChancePage = Boolean(document.querySelector('[data-geo-second-chance-page]'));
    if (shouldHide && isSecondChancePage && !geoSecondChanceState.hasRedirected) {
      geoSecondChanceState.hasRedirected = true;
      window.location.replace(secondChanceRedirectUrl);
      return;
    }

    const isProductPage = window.location.pathname.toLowerCase().includes('/products/');
    const hasRestrictedProductTagOnPage = Boolean(
      document.querySelector(
        '[data-section-id][data-product-tag-type="second-chance"], [data-section-id][data-product-tag-type="discontinued-stock"]'
      )
    );
    if (shouldHide && isProductPage && hasRestrictedProductTagOnPage && !geoSecondChanceState.hasRedirected) {
      geoSecondChanceState.hasRedirected = true;
      window.location.replace(secondChanceRedirectUrl);
    }
  }

  function ensureSecondChanceObserver() {
    if (geoSecondChanceMutationObserver || typeof MutationObserver === 'undefined' || !document.body) return;

    geoSecondChanceMutationObserver = new MutationObserver((mutations) => {
      if (!geoSecondChanceState.isHidden) return;
      const hasAddedNodes = mutations.some(
        (mutation) => mutation.type === 'childList' && mutation.addedNodes && mutation.addedNodes.length > 0
      );
      if (!hasAddedNodes) return;

      if (geoSecondChanceMutationTimer) clearTimeout(geoSecondChanceMutationTimer);
      geoSecondChanceMutationTimer = setTimeout(() => {
        applySecondChanceVisibility(true);
      }, 80);
    });

    geoSecondChanceMutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function getCachedIpCountry() {
    try {
      return window.dbodhi_geo_ip_country || '';
    } catch (e) {
      return '';
    }
  }

  function setCachedIpCountry(countryCode) {
    try {
      window.dbodhi_geo_ip_country = countryCode;
    } catch (e) { }
  }

  function applyGeoRulesWithCountry(countryCode) {
    const hasAllowlist = Array.isArray(allowedCountries) && allowedCountries.length > 0;
    const hasSecondChanceDenylist =
      Array.isArray(hiddenSecondChanceCountries) && hiddenSecondChanceCountries.length > 0;

    const canSeePrice = hasAllowlist ? allowedCountries.includes(countryCode) : false;
    const hideSecondChance = hasSecondChanceDenylist
      ? hiddenSecondChanceCountries.includes(countryCode)
      : false;

    // console.log("Geo Restrictions Debug:", {
    //   detectedCountry: countryCode,
    //   allowedCountries: allowedCountries,
    //   canSeePrice: canSeePrice,
    //   hasAllowlist: hasAllowlist,
    //   shopifyCountryCode: shopifyCountryCode,
    //   bodyCountry: document.body.dataset.shopifyCountry
    // });

    if (hasAllowlist) {
      applyGeoPriceVisibility(canSeePrice);
    }

    if (hasSecondChanceDenylist) {
      applySecondChanceVisibility(hideSecondChance);
    }
  }

  function applyGeoRulesFromShopifyCountry() {
    ensureSecondChanceObserver();
    const hasAllowlist = Array.isArray(allowedCountries) && allowedCountries.length > 0;
    const hasSecondChanceDenylist =
      Array.isArray(hiddenSecondChanceCountries) && hiddenSecondChanceCountries.length > 0;

    if (!hasAllowlist) {
      applyGeoPriceVisibility(false);
    }

    if (!hasSecondChanceDenylist) {
      applySecondChanceVisibility(false);
    }

    if (!hasAllowlist && !hasSecondChanceDenylist) {
      return;
    }

    const detectedCountry = String(
      shopifyCountryCode || document.body.dataset.shopifyCountry || ''
    ).toUpperCase();

    if (detectedCountry === 'NL') {
      const cachedCountry = getCachedIpCountry();
      if (cachedCountry) {
        applyGeoRulesWithCountry(cachedCountry);
      } else {
        // Apply default NL rules synchronously first
        applyGeoRulesWithCountry('NL');

        // Double-check with ipdata.co API
        const apiKey = '65aca6c7e3cc4750d90e2470634a3a5d27d236d024b9a6157eaff8e0';
        fetch('https://api.ipdata.co?api-key=' + apiKey)
          .then((response) => response.json())
          .then((data) => {
            if (data && data.country_code) {
              const realCountry = String(data.country_code).toUpperCase();
              setCachedIpCountry(realCountry);
              if (realCountry !== 'NL') {
                applyGeoRulesWithCountry(realCountry);
              }
            }
          })
          .catch((err) => {
            console.error('Failed to fetch IP country:', err);
          });
      }
    } else {
      applyGeoRulesWithCountry(detectedCountry);
    }
  }

  function installCartGuards() {
    document.addEventListener(
      'submit',
      function (event) {
        if (!geoPurchaseRestrictionState.isRestricted) return;
        const targetForm = event.target;
        if (!shouldBlockAddToCartForm(targetForm)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        syncGeoPurchaseRestrictionMessage(true);
      },
      true
    );

    if (!window.__geoCartAddFetchGuardApplied) {
      window.__geoCartAddFetchGuardApplied = true;
      const originalFetch = window.fetch;
      if (typeof originalFetch === 'function') {
        window.fetch = function (input, init) {
          const requestUrl = parseRequestUrl(input) || (init && init.url) || '';
          if (geoPurchaseRestrictionState.isRestricted && isCartAddUrl(requestUrl)) {
            return Promise.resolve(
              new Response(
                JSON.stringify({
                  status: 403,
                  message: geoPurchaseRestrictionState.message,
                }),
                {
                  status: 403,
                  headers: { 'Content-Type': 'application/json' },
                }
              )
            );
          }
          return originalFetch.apply(this, arguments);
        };
      }
    }

    if (!window.__geoCartAddXhrGuardApplied && typeof XMLHttpRequest !== 'undefined') {
      window.__geoCartAddXhrGuardApplied = true;
      const originalXhrOpen = XMLHttpRequest.prototype.open;
      const originalXhrSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url) {
        this.__geoRequestUrl = url;
        return originalXhrOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function () {
        if (geoPurchaseRestrictionState.isRestricted && isCartAddUrl(this.__geoRequestUrl)) {
          this.abort();
          return;
        }
        return originalXhrSend.apply(this, arguments);
      };
    }
  }

  function init() {
    installCartGuards();
    applyGeoRulesFromShopifyCountry();
  }

  if (document.body) {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
